import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isV255User, ownsAddon } from '@/lib/featureFlags';
import { APP_VERSION } from '@/lib/version';
import NotificationBell from '@/components/NotificationBell';
import TrialCardBanner from '@/components/TrialCardBanner';
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  Users,
  FileBox,
  TrendingUp,
  Settings as SettingsIcon,
  User,
  Building2,
  ChevronLeft,
  ChevronRight,
  LogOut,
  CreditCard,
  HelpCircle,
  Calculator,
  Calendar,
  Sparkles,
  Landmark,
  Mail,
  Send,
  Plus,
  Menu as MenuIcon,
} from 'lucide-react';
import { DemoRequestModal } from '@/components/DemoRequestModal';

// Tracks whether we're at desktop width (>=1024px). Drives the sidebar's
// fixed-vs-drawer behavior so the shell is responsive on phones/tablets.
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, []);
  return isDesktop;
}

// Full nav items for owners/admins
const allNavItems = [
  { 
    id: 'dashboard',
    label: 'Dashboard', 
    path: '/dashboard', 
    icon: LayoutDashboard,
    description: 'Overview & projects',
    allowedRoles: ['owner', 'project_manager', 'accountant', 'investor'] // Everyone can see dashboard
  },
  { 
    id: 'project-hub',
    label: 'Project Hub', 
    path: '/project-hub', 
    icon: FolderKanban,
    description: 'Contractors & documents',
    addon: true,
    allowedRoles: ['owner', 'project_manager'] // Only owners and PMs
  },
  {
    id: 'contractors',
    label: 'Contractors',
    path: '/contractors',
    icon: Users,
    description: 'Your contractor roster',
    allowedRoles: ['owner', 'project_manager', 'accountant']
  },
  { 
    id: 'schedule',
    label: 'Schedule', 
    path: '/schedule', 
    icon: Calendar,
    description: 'Funded & scheduled projects',
    addon: true,
    allowedRoles: ['owner', 'project_manager', 'accountant']
  },
  { 
    id: 'reports',
    label: 'Reports', 
    path: '/reports', 
    icon: FileText,
    description: 'Draw requests & reports',
    addon: true,
    allowedRoles: ['owner', 'project_manager', 'accountant'] // Not investors
  },
  {
    id: 'lenders',
    label: 'Lenders',
    path: '/lenders',
    icon: Landmark,
    description: 'Your banks & their forms',
    addon: true,
    allowedRoles: ['owner', 'project_manager', 'accountant'] // Not investors
  },
  // Financial Analysis is now accessible via the Command Center "Underwrite a Deal" card.
  // The /financial-analysis route still works for direct links and team-member permissions,
  // but it has been removed from the sidebar to reduce clutter (Feb 2026).
];

const allBottomNavItems = [
  {
    label: "What's new",
    path: '/whats-new',
    icon: Sparkles,
    description: 'Recent product updates',
    allowedRoles: ['owner', 'project_manager', 'accountant', 'investor'] // Everyone
  },
  {
    label: 'Demo leads',
    path: '/demo-leads',
    icon: Send,
    description: 'Prospects you sent a demo to',
    allowedRoles: ['owner'] // Owner-only outreach tracker
  },
  { 
    label: 'Settings', 
    path: '/settings', 
    icon: SettingsIcon,
    description: 'Cascade test mode & preferences',
    allowedRoles: ['owner'] // Owner-only — controls cascade emails
  },
  { 
    label: 'Profile', 
    path: '/profile', 
    icon: User,
    description: 'Account settings',
    allowedRoles: ['owner', 'project_manager', 'accountant', 'investor'] // Everyone
  },
];

export const Sidebar = ({ user, onLogout, collapsed, setCollapsed, userRoles = [], customPermissions = [], onStartTour, mobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const [isHovering, setIsHovering] = useState(false);
  const [demoLinkOpen, setDemoLinkOpen] = useState(false);
  const [lenderDemoOpen, setLenderDemoOpen] = useState(false);
  
  // Determine user's effective role for filtering
  const isTeamMember = userRoles.length > 0;
  const isInvestor = userRoles.includes('investor');
  const effectiveRole = isTeamMember ? userRoles[0] : 'owner';
  // Owner-only sales tools: appear ONLY on the DrawReady creator's account
  // (flagged server-side via feature_flags.demo_owner_tools). NOT for regular
  // customers, team members, investors, or demo accounts.
  const isDemoOwner = !!user?.feature_flags?.demo_owner_tools;
  
  // Map nav paths to permission IDs
  const pathToPermission = {
    '/dashboard': 'view_dashboard',
    '/project-hub': 'view_project_hub',
    '/reports': 'view_reports',
    '/analysis-suite': 'view_financial_analysis',
    '/underwrite': 'view_financial_analysis',
    '/financial-analysis': 'view_financial_analysis',
    '/bank-templates': 'view_bank_templates',
    '/profile': null, // Always allowed
    '/settings': null // Owner-only enforced via allowedRoles
  };
  
  // Filter nav items based on user role AND custom permissions (for investors)
  const navItems = allNavItems.filter(item => {
    // First check role
    if (!item.allowedRoles.includes(effectiveRole)) return false;
    // Creator-only demo tooling — hide from every regular account owner + demo user.
    if (item.path === '/demo-leads' && !isDemoOwner) return false;
    
    // For investors with custom permissions, also check permissions
    if (isInvestor && customPermissions.length > 0) {
      const requiredPermission = pathToPermission[item.path];
      if (requiredPermission && !customPermissions.includes(requiredPermission)) {
        return false;
      }
    }
    
    return true;
  });

  // Add-on modules the user hasn't bought are removed from the nav entirely.
  // Instead, non-owners who are missing any add-on get one enticing entry that
  // opens the store — keeps the base app lean and drives curiosity.
  const ownsEveryAddon = ['project-hub', 'schedule', 'reports', 'project-tools'].every((id) => ownsAddon(user, id));
  let filteredNavItems = navItems.filter((item) => !item.addon || ownsAddon(user, item.id));
  if (!ownsEveryAddon) {
    filteredNavItems = [
      ...filteredNavItems,
      { id: 'add-ons', label: 'Unlock more tools', path: '/add-ons', icon: Sparkles, description: 'Add Project Hub, Reports & more', isStore: true, allowedRoles: [] },
    ];
  }
  
  const bottomNavItems = allBottomNavItems.filter(item => {
    if (!item.allowedRoles.includes(effectiveRole)) return false;
    // Creator-only demo tooling — hide from every regular account owner + demo user.
    if (item.path === '/demo-leads' && !isDemoOwner) return false;
    
    if (isInvestor && customPermissions.length > 0) {
      const requiredPermission = pathToPermission[item.path];
      if (requiredPermission && !customPermissions.includes(requiredPermission)) {
        return false;
      }
    }
    
    return true;
  });
  
  // Determine if sidebar should be expanded (either not collapsed or hovering).
  // On mobile the drawer is always full-width (labels shown).
  const isDesktop = useIsDesktop();
  const isExpanded = !isDesktop ? true : (!collapsed || isHovering);
  const closeOnMobile = () => { if (!isDesktop && onMobileClose) onMobileClose(); };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside 
      className={`fixed left-0 top-0 h-full bg-slate-900 text-white z-50 transition-all duration-300 flex flex-col ${
        isExpanded ? 'w-64' : 'w-20'
      } ${isDesktop ? 'translate-x-0' : (mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full')}`}
      onMouseEnter={() => isDesktop && setIsHovering(true)}
      onMouseLeave={() => isDesktop && setIsHovering(false)}
    >
      {/* Logo/Brand */}
      <div className="p-4 border-b border-slate-700">
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-lg">D</span>
          </div>
          {isExpanded && (
            <div className="overflow-hidden">
              <h1 className="font-bold text-lg text-white whitespace-nowrap">DrawReady</h1>
              <p className="text-xs text-slate-400 whitespace-nowrap">Construction Finance</p>
            </div>
          )}
        </Link>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-3">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const isHero = item.isStore === true;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  data-sidebar-item={item.id}
                  onClick={closeOnMobile}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group relative ${
                    isHero
                      ? `text-white shadow-lg shadow-purple-900/30 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 ${active ? 'ring-2 ring-purple-300/60' : ''}`
                      : active
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  title={!isExpanded ? item.label : undefined}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${(active || isHero) ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} />
                  {isExpanded && (
                    <div className="overflow-hidden">
                      <span className="font-medium whitespace-nowrap">{item.label}</span>
                      {(active || isHero) && (
                        <p className={`text-xs whitespace-nowrap ${isHero ? 'text-purple-100' : 'text-blue-200'}`}>{item.description}</p>
                      )}
                    </div>
                  )}
                  {!isExpanded && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                      {item.label}
                    </div>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-slate-700 py-4">
        <ul className="space-y-1 px-3">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={closeOnMobile}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group relative ${
                    active 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  title={!isExpanded ? item.label : undefined}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} />
                  {isExpanded && (
                    <span className="font-medium whitespace-nowrap">{item.label}</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* User & Logout */}
        {user && (
          <div className="mt-4 px-3">
            <div className={`flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800 ${isExpanded ? '' : 'justify-center'}`}>
              <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-medium">
                  {(user.name || user.email || '?')[0].toUpperCase()}
                </span>
              </div>
              {isExpanded && (
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
                    {isV255User(user) && (
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm leading-none flex-shrink-0"
                        title={`You're on the ${APP_VERSION} early-access build`}
                        data-testid="beta-pill"
                      >
                        Beta
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 truncate">{user.email}</p>
                  <p className="text-[10px] text-slate-500 truncate mt-0.5" data-testid="app-version">
                    DrawReady {APP_VERSION}
                  </p>
                </div>
              )}
            </div>
            {user?.id && (
              <div className="mt-2" data-testid="sidebar-notifications">
                <NotificationBell userId={user.id} variant="sidebar" expanded={isExpanded} />
              </div>
            )}
            <button
              onClick={onLogout}
              className={`mt-2 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all ${isExpanded ? '' : 'justify-center'}`}
              title={!isExpanded ? 'Logout' : undefined}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {isExpanded && <span className="text-sm">Logout</span>}
            </button>
            
            {/* Take a Tour Button */}
            {onStartTour && (
              <button
                onClick={onStartTour}
                className={`mt-2 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-blue-500/10 hover:text-blue-400 transition-all ${isExpanded ? '' : 'justify-center'}`}
                title={!isExpanded ? 'Take a Tour' : undefined}
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                {isExpanded && <span className="text-sm">Take a Tour</span>}
              </button>
            )}

            {/* Send demo link — owner-only sales tool (hidden from team members/investors) */}
            {isDemoOwner && (
              <button
                onClick={() => setDemoLinkOpen(true)}
                className={`mt-2 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-emerald-500/10 hover:text-emerald-400 transition-all ${isExpanded ? '' : 'justify-center'}`}
                title={!isExpanded ? 'Send demo link' : undefined}
                data-testid="sidebar-send-demo-link-btn"
              >
                <Mail className="w-5 h-5 flex-shrink-0" />
                {isExpanded && <span className="text-sm">Send demo link</span>}
              </button>
            )}

            {/* Send lender demo — owner-only: emails a lender the read-only portfolio
                demo + a forwardable borrower demo for their clients */}
            {isDemoOwner && (
              <button
                onClick={() => setLenderDemoOpen(true)}
                className={`mt-2 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-sky-500/10 hover:text-sky-400 transition-all ${isExpanded ? '' : 'justify-center'}`}
                title={!isExpanded ? 'Send lender demo' : undefined}
                data-testid="sidebar-send-lender-demo-btn"
              >
                <Building2 className="w-5 h-5 flex-shrink-0" />
                {isExpanded && <span className="text-sm">Send lender demo</span>}
              </button>
            )}
          </div>
        )}
      </div>

      <DemoRequestModal
        open={demoLinkOpen}
        onClose={() => setDemoLinkOpen(false)}
        title="Send a demo link"
        subtitle="Email a prospect the interactive demo link + passcode."
        submitLabel="Send demo link"
        successTitle="Demo link sent"
        footnote="We'll email them the demo link and passcode from noreply@drawready.com."
      />

      <DemoRequestModal
        open={lenderDemoOpen}
        onClose={() => setLenderDemoOpen(false)}
        endpoint="/lender-demo/invite"
        title="Send lender demo"
        subtitle="Email a lender the read-only portfolio demo + a borrower demo they can forward to clients."
        submitLabel="Send lender demo"
        successTitle="Lender demo sent"
        companyLabel="Institution"
        footnote="We'll email them the lender portfolio demo, plus a borrower demo link they can share with their clients — from noreply@drawready.com."
      />

      {/* Collapse Toggle (desktop only) */}
      {isDesktop && (
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-slate-700 hover:bg-slate-600 rounded-full flex items-center justify-center text-slate-300 hover:text-white transition-all border border-slate-600"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      )}
    </aside>
  );
};

// Layout wrapper that includes sidebar
export const SidebarLayout = ({ children, user, onLogout, userRoles = [], customPermissions = [], onStartTour }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Auto-close the mobile drawer on route change.
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile top bar with hamburger (hidden on desktop) */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-slate-900 text-white z-40 flex items-center gap-3 px-4 shadow-md">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-1.5 rounded-lg hover:bg-slate-800 transition"
          aria-label="Open menu"
          data-testid="mobile-menu-toggle"
        >
          <MenuIcon className="w-6 h-6" />
        </button>
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold">D</span>
          </div>
          <span className="font-bold text-white">DrawReady</span>
        </Link>
      </div>

      {/* Dark overlay behind the drawer (mobile only) */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-menu-overlay"
        />
      )}

      <Sidebar 
        user={user} 
        onLogout={onLogout} 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        userRoles={userRoles}
        customPermissions={customPermissions}
        onStartTour={onStartTour}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <main className={`transition-all duration-300 pt-14 lg:pt-0 ml-0 ${collapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <TrialCardBanner user={user} />
        {children}
      </main>
    </div>
  );
};

export default Sidebar;
